import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fetchSeasonStats } from '../api/seasonStats';
import SeasonHero from './season/SeasonHero';
import SeasonTeamStats from './season/SeasonTeamStats';
import SeasonLeaders from './season/SeasonLeaders';
import SeasonRunDiff from './season/SeasonRunDiff';
import SeasonWinLossTrend from './season/SeasonWinLossTrend';
import SeasonMilestones from './season/SeasonMilestones';
import PlayerProfile from './PlayerProfile';
import TeamPerformanceAnalytics from './analytics/TeamPerformanceAnalytics';

const { FiRefreshCw, FiTrendingUp, FiBarChart2 } = FiIcons;

const SeasonOverview = ({ standings, games }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchSeasonStats(111);
      setStats(data);
    } catch (e) {
      console.error('Season stats fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handlePlayerClick = (id, name) => setSelectedPlayer({ id, name });

  return (
    <>
      <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.05)] w-full">
        {/* Header */}
        <div className="p-3 sm:p-5 border-b-2 border-[#658d71] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiTrendingUp} size={18} className="text-[#a9c9b1]" />
            <div>
              <h3 className="text-base sm:text-xl font-bold tracking-widest text-[#d8ceb3] uppercase">Season Overview</h3>
              <p className="text-[9px] text-[#658d71] tracking-widest uppercase font-bold mt-0.5">
                {new Date().getFullYear()} Boston Red Sox Season
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button 
              onClick={() => setShowAnalytics(true)}
              className="flex items-center gap-1.5 border border-[#a9c9b1] text-[#a9c9b1] hover:bg-[#386646] hover:text-white text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 transition-colors"
            >
              <SafeIcon icon={FiBarChart2} size={12} />
              Team Analytics
            </button>
            <button 
              onClick={() => setExpanded(v => !v)}
              className="text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 border border-[#658d71] text-[#a9c9b1] hover:text-white hover:border-[#a9c9b1] transition-colors"
            >
              {expanded ? 'Collapse' : 'Expand All'}
            </button>
            <button 
              onClick={load} 
              disabled={loading}
              className="flex items-center gap-1.5 border border-[#658d71] text-[#a9c9b1] hover:text-white hover:border-[#a9c9b1] text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} size={12} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        <div className="p-3 sm:p-5 flex flex-col gap-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-3">
              <div className="w-7 h-7 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
              <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading season data...</span>
            </div>
          ) : (
            <AnimatePresence>
              <motion.div className="flex flex-col gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
                <SeasonHero standings={standings} />
                <SeasonWinLossTrend games={games} />
                <SeasonMilestones games={games} />
                
                <AnimatePresence>
                  {expanded && (
                    <motion.div 
                      className="flex flex-col gap-4"
                      initial={{ opacity: 0, height: 0 }} 
                      animate={{ opacity: 1, height: 'auto' }} 
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4">
                        <SeasonTeamStats teamStats={stats?.teamStats} />
                        <SeasonLeaders 
                          leadersBatting={stats?.leadersBatting} 
                          leadersPitching={stats?.leadersPitching} 
                          onPlayerClick={handlePlayerClick}
                        />
                      </div>
                      <SeasonRunDiff standings={standings} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {showAnalytics && <TeamPerformanceAnalytics onClose={() => setShowAnalytics(false)} />}
      
      {selectedPlayer && (
        <PlayerProfile 
          playerId={selectedPlayer.id} 
          playerName={selectedPlayer.name} 
          onClose={() => setSelectedPlayer(null)} 
        />
      )}
    </>
  );
};

export default SeasonOverview;